import React from "react";
import { useApp } from "../context/AppContext";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Loader2, Database } from "lucide-react";

export function Brand({ size = "md", dark = false, tagline = false, className = "" }) {
  const marks = { xs: "h-7", sm: "h-8", md: "h-10", lg: "h-14", xl: "h-16" };
  const texts = { xs: "text-base", sm: "text-lg", md: "text-xl", lg: "text-3xl", xl: "text-4xl" };
  return (
    <div className={`flex items-center gap-2.5 ${className}`}>
      <div className={`${marks[size]} aspect-square rounded-2xl bg-white shadow-sm ring-1 ring-black/5 flex items-center justify-center p-1.5 shrink-0`}>
        <img src="/proclytic-mark.png" alt="ProClytic" className="h-full w-full object-contain" />
      </div>
      <div className="leading-none">
        <div className={`font-display font-extrabold ${texts[size]} tracking-tight ${dark ? "text-white" : "text-[#0f2547]"}`}>ProClytic</div>
        {tagline && <div className={`text-[9px] tracking-[0.22em] uppercase mt-1 font-body ${dark ? "text-white/55" : "text-slate-400"}`}>Process Intelligence</div>}
      </div>
    </div>
  );
}

export function Logo({ className = "h-10" }) {
  return <img src="/proclytic-mark.png" alt="ProClytic" className={`${className} w-auto object-contain`} />;
}

export function GlassCard({ className = "", children, hover = false, ...props }) {
  return (
    <div className={`glass rounded-3xl pm-transition ${hover ? "pm-lift cursor-default" : ""} ${className}`} {...props}>
      {children}
    </div>
  );
}

export function SolidCard({ className = "", children, ...props }) {
  return (
    <div className={`solid-card rounded-3xl ${className}`} {...props}>{children}</div>
  );
}

export function SectionTitle({ eyebrow, title, sub, right }) {
  return (
    <div className="flex items-end justify-between gap-4 mb-5">
      <div>
        {eyebrow && <div className="text-xs font-semibold tracking-[0.18em] uppercase text-[#0e7aa6] mb-1.5">{eyebrow}</div>}
        <h2 className="text-2xl md:text-3xl font-bold text-[#0f2547]">{title}</h2>
        {sub && <p className="text-sm text-slate-500 mt-1 font-body">{sub}</p>}
      </div>
      {right}
    </div>
  );
}

const IMPACT = {
  High: "bg-emerald-100 text-emerald-700",
  Medium: "bg-amber-100 text-amber-700",
  Low: "bg-slate-100 text-slate-600",
};
const SEV = {
  high: "bg-red-100 text-red-700 border-red-200",
  medium: "bg-amber-100 text-amber-700 border-amber-200",
  low: "bg-sky-100 text-sky-700 border-sky-200",
};
const STATUS = {
  connected: "bg-emerald-100 text-emerald-700",
  active: "bg-emerald-100 text-emerald-700",
  syncing: "bg-sky-100 text-sky-700",
  error: "bg-red-100 text-red-700",
  suspended: "bg-red-100 text-red-700",
  open: "bg-red-100 text-red-700",
  acknowledged: "bg-amber-100 text-amber-700",
  new: "bg-cyan-100 text-cyan-700",
  in_progress: "bg-sky-100 text-sky-700",
};

export function Badge({ kind = "status", value, className = "" }) {
  const map = kind === "impact" ? IMPACT : kind === "sev" ? SEV : STATUS;
  const cls = map[value] || "bg-slate-100 text-slate-600";
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold capitalize border border-transparent ${cls} ${className}`}>
      {String(value).replace(/_/g, " ")}
    </span>
  );
}

export function Loading({ label = "Loading process data…" }) {
  return (
    <div className="flex flex-col items-center justify-center py-24 text-slate-400" data-testid="loading-state">
      <Loader2 className="w-7 h-7 animate-spin text-cyan-600 mb-3" />
      <p className="text-sm">{label}</p>
    </div>
  );
}

export function EmptyState({ title, sub, icon: Icon = Database, action }) {
  return (
    <div className="flex flex-col items-center justify-center py-20 text-center" data-testid="empty-state">
      <div className="w-14 h-14 rounded-2xl pm-gradient flex items-center justify-center mb-4 shadow-lg shadow-cyan-500/20">
        <Icon className="w-7 h-7 text-white" />
      </div>
      <h3 className="text-lg font-bold text-slate-800">{title}</h3>
      {sub && <p className="text-sm text-slate-500 mt-1 max-w-md">{sub}</p>}
      {action && <div className="mt-5">{action}</div>}
    </div>
  );
}

export function DatasetSelector({ className = "" }) {
  const { datasets, datasetId, setDatasetId } = useApp();
  if (!datasets.length) return null;
  return (
    <Select value={datasetId || ""} onValueChange={setDatasetId}>
      <SelectTrigger className={`glass border-white/60 rounded-xl h-10 min-w-[220px] font-medium ${className}`} data-testid="dataset-selector">
        <div className="flex items-center gap-2">
          <Database className="w-4 h-4 text-[#0e7aa6]" />
          <SelectValue placeholder="Select dataset" />
        </div>
      </SelectTrigger>
      <SelectContent>
        {datasets.map((d) => (
          <SelectItem key={d.id} value={d.id} data-testid={`dataset-option-${d.id}`}>
            {d.name} · {d.domain}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

export function Gauge({ value = 0, label, size = 150, color = "#0e7aa6" }) {
  const r = size / 2 - 14;
  const c = Math.PI * r; // half circle circumference
  const pct = Math.max(0, Math.min(100, value));
  const offset = c - (pct / 100) * c;
  return (
    <div className="flex flex-col items-center" style={{ width: size }}>
      <svg width={size} height={size / 2 + 12} viewBox={`0 0 ${size} ${size / 2 + 12}`}>
        <path d={`M14 ${size / 2} A ${r} ${r} 0 0 1 ${size - 14} ${size / 2}`} fill="none" stroke="#e2e8f0" strokeWidth="12" strokeLinecap="round" />
        <path d={`M14 ${size / 2} A ${r} ${r} 0 0 1 ${size - 14} ${size / 2}`} fill="none" stroke={color} strokeWidth="12" strokeLinecap="round"
          strokeDasharray={c} strokeDashoffset={offset} style={{ transition: "stroke-dashoffset 1s cubic-bezier(0.16,1,0.3,1)" }} />
      </svg>
      <div className="-mt-8 text-center">
        <div className="text-2xl font-bold text-slate-900 font-display">{pct}%</div>
        {label && <div className="text-xs text-slate-500 mt-0.5">{label}</div>}
      </div>
    </div>
  );
}

export function fmtHours(h) {
  if (h == null) return "—";
  if (h < 1) return `${Math.round(h * 60)}m`;
  if (h < 48) return `${h.toFixed(1)}h`;
  return `${(h / 24).toFixed(1)}d`;
}

export function fmtNum(n) {
  if (n == null) return "—";
  if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
  if (n >= 1000) return `${(n / 1000).toFixed(1)}k`;
  return n.toString();
}
